// The app's modal dialogs, extracted from App so it can stay a thin controller.
// Each presenter builds its markup, mounts it via openModal (which owns the
// shared backdrop/Escape/cleanup behaviour), and wires its own buttons. None of
// them touch App state directly: the two that need it (rename, share actions)
// take their input as arguments and hand results back through callbacks.
import { BADGES } from "../data/badges";
import { MAX_TEAM_NAME_LEN } from "../engine/replay";
import { setOptedOut } from "../state/leaderboard";
import { esc } from "./helpers";
import { openModal } from "./modal";
import {
  canCopyImage,
  canDeviceShare,
  copyImageToClipboard,
  downloadImage,
  shareViaDevice,
  type ResultImage,
} from "./share";
import { getLang, t } from "./i18n";

/** "How to play" overlay: shown once on first run, re-openable from the menu. */
export function openHowToModal(): void {
  const T = t();
  const steps = T.howToSteps
    .map(
      (s) =>
        `<div class="howStep"><h4>${esc(s.h)}</h4><p>${esc(
          s.p.replace("{badgeCount}", String(BADGES.length)),
        )}</p></div>`,
    )
    .join("");
  const { overlay, close } = openModal(`<div class="modal howModal" role="dialog" aria-modal="true" aria-label="${esc(T.howToTitle)}">
      <h3 class="modalTitle">${esc(T.howToTitle)}</h3>
      <div class="howSteps">${steps}</div>
      <div class="modalActions"><button class="btn primary" id="howClose">${esc(T.howToCta)}</button></div>
    </div>`);
  overlay.querySelector<HTMLButtonElement>("#howClose")?.addEventListener("click", close);
  // preventScroll: the CTA sits at the bottom of a scrollable modal, so a
  // plain focus() would scroll it into view and open the dialog at the bottom.
  // Keep it keyboard-focused but let the modal start at the top.
  overlay.querySelector<HTMLButtonElement>("#howClose")?.focus({ preventScroll: true });
}

/**
 * "Send a suggestion" overlay: posts free text to the Worker's /api/suggest
 * route, which forwards it to our inbox via Resend. The hidden "company"
 * field is a honeypot — bots fill it, humans never see it, and the Worker
 * silently drops any request that has it populated.
 */
export function openSuggestModal(): void {
  const T = t();
  const { overlay, close } = openModal(`<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(T.suggestTitle)}">
      <h3 class="modalTitle">${esc(T.suggestTitle)}</h3>
      <p class="sub">${esc(T.suggestSub)}</p>
      <textarea id="suggestText" class="suggestText" rows="5" maxlength="2000" placeholder="${esc(T.suggestPlaceholder)}"></textarea>
      <input type="text" id="suggestCompany" class="hp" tabindex="-1" autocomplete="off" aria-hidden="true" />
      <p class="suggestMsg" id="suggestMsg" role="status" aria-live="polite"></p>
      <div class="modalActions">
        <button class="btn ghost" id="suggestCancel">${esc(T.confirmCancel)}</button>
        <button class="btn primary" id="suggestSend">${esc(T.suggestSend)}</button>
      </div>
    </div>`);

  const textEl = overlay.querySelector<HTMLTextAreaElement>("#suggestText");
  const hpEl = overlay.querySelector<HTMLInputElement>("#suggestCompany");
  const msgEl = overlay.querySelector<HTMLParagraphElement>("#suggestMsg");
  const sendBtn = overlay.querySelector<HTMLButtonElement>("#suggestSend");
  overlay.querySelector<HTMLButtonElement>("#suggestCancel")?.addEventListener("click", close);
  textEl?.focus();

  const send = async (): Promise<void> => {
    if (!textEl || !sendBtn || !msgEl) return;
    const text = textEl.value.trim();
    if (!text) {
      msgEl.textContent = T.suggestEmpty;
      msgEl.className = "suggestMsg err";
      return;
    }
    sendBtn.disabled = true;
    sendBtn.textContent = T.suggestSending;
    msgEl.textContent = "";
    msgEl.className = "suggestMsg";
    try {
      const res = await fetch("/api/suggest", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text, lang: getLang(), company: hpEl?.value ?? "" }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      msgEl.textContent = T.suggestThanks;
      msgEl.className = "suggestMsg ok";
      textEl.disabled = true;
      sendBtn.textContent = T.suggestSend;
      // Leave the success state on screen; auto-dismiss after a beat.
      window.setTimeout(close, 1800);
    } catch {
      msgEl.textContent = T.suggestError;
      msgEl.className = "suggestMsg err";
      sendBtn.disabled = false;
      sendBtn.textContent = T.suggestSend;
    }
  };
  sendBtn?.addEventListener("click", () => void send());
}

// One-time, non-blocking notice the first time a result is posted, since the
// team name becomes publicly visible. Offers a future-only opt-out.
export function openLeaderboardNoticeModal(): void {
  const T = t();
  const { overlay, close } = openModal(`<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(T.lbNoticeTitle)}">
      <h3 class="modalTitle">${esc(T.lbNoticeTitle)}</h3>
      <p class="sub">${esc(T.lbNoticeBody)}</p>
      <div class="modalActions">
        <button class="btn ghost" id="lbOptOut">${esc(T.lbNoticeOptOut)}</button>
        <button class="btn primary" id="lbOk">${esc(T.lbNoticeOk)}</button>
      </div>
    </div>`);
  overlay.querySelector<HTMLButtonElement>("#lbOk")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("#lbOptOut")?.addEventListener("click", () => {
    setOptedOut(true);
    close();
  });
  overlay.querySelector<HTMLButtonElement>("#lbOk")?.focus({ preventScroll: true });
}

/**
 * Edit the persisted team name. `currentName` pre-fills the input; the trimmed,
 * capped result is handed back via `onSave` (the caller owns persistence and
 * re-rendering). Reachable from the menu without starting a season.
 */
export function openRenameTeamModal(currentName: string, onSave: (name: string) => void): void {
  const T = t();
  const { overlay, close } = openModal(`<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(T.nameTitle)}">
      <h3 class="modalTitle">${esc(T.nameTitle)}</h3>
      <p class="sub">${esc(T.nameSub)}</p>
      <input id="renameInput" type="text" maxlength="${MAX_TEAM_NAME_LEN}" value="${esc(currentName)}" placeholder="${esc(T.namePlaceholder)}" style="width:100%" />
      <p class="hint">${esc(T.nameHint)}</p>
      <div class="modalActions">
        <button class="btn ghost" id="renameCancel">${esc(T.confirmCancel)}</button>
        <button class="btn primary" id="renameSave">${esc(T.nameSave)}</button>
      </div>
    </div>`);
  const input = overlay.querySelector<HTMLInputElement>("#renameInput");
  const save = (): void => {
    onSave((input?.value ?? "").trim().slice(0, MAX_TEAM_NAME_LEN));
    close();
  };
  overlay.querySelector<HTMLButtonElement>("#renameCancel")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("#renameSave")?.addEventListener("click", save);
  input?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") save();
  });
  window.setTimeout(() => input?.focus(), 50);
}

/**
 * A styled confirmation dialog, replacing the native confirm() for the
 * destructive actions (reset progress, start over, forfeit). onConfirm runs only
 * on accept; dismissing (Cancel, Escape, backdrop click) is a no-op. Focus lands
 * on Cancel — the safe default for a destructive prompt.
 */
export function confirmModal(message: string, onConfirm: () => void): void {
  const T = t();
  const { overlay, close } = openModal(`<div class="modal confirmModal" role="alertdialog" aria-modal="true" aria-label="${esc(message)}">
      <p class="confirmMsg">${esc(message)}</p>
      <div class="modalActions">
        <button class="btn primary" id="cfmYes">${esc(T.confirmYes)}</button>
        <button class="btn ghost" id="cfmNo">${esc(T.confirmCancel)}</button>
      </div>
    </div>`);
  overlay.querySelector<HTMLButtonElement>("#cfmNo")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("#cfmYes")?.addEventListener("click", () => {
    close();
    onConfirm();
  });
  overlay.querySelector<HTMLButtonElement>("#cfmNo")?.focus();
}

// Daily share is a text-only scoreboard the player can fire at a friend.
// No result image: everyone gets the same draft pool, so an image would
// reveal the lineup and let a friend copy the picks. Offers the native share
// sheet where available, plus copy-to-clipboard, with the message in view.
export function openDailyShareModal(text: string): void {
  const T = t();
  const canShareText = typeof navigator.share === "function";
  const shareBtn = canShareText ? `<button class="btn primary" id="dShare">${T.shareVia}</button>` : "";
  const { overlay, close } = openModal(`<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(T.shareDailyTitle)}">
      <h3 class="modalTitle">${esc(T.shareDailyTitle)}</h3>
      <p class="shareMsg" style="white-space:pre-wrap;word-break:break-word;text-align:left;background:var(--panel2);border:1px solid var(--line);border-radius:10px;padding:14px;font-size:14px">${esc(text)}</p>
      <div class="modalActions">
        ${shareBtn}
        <button class="btn" id="dCopy">${T.shareCopyMessage}</button>
      </div>
      <button class="btn ghost modalClose" id="dClose">${T.shareClose}</button>
    </div>`);
  overlay.querySelector<HTMLButtonElement>("#dClose")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("#dShare")?.addEventListener("click", async () => {
    try {
      await navigator.share({ title: "Grand Prix Draft Legends", text });
      close();
    } catch {
      // User dismissed the sheet or it failed — leave the modal open to copy.
    }
  });
  overlay.querySelector<HTMLButtonElement>("#dCopy")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      ok = false;
    }
    b.textContent = ok ? T.shareCopied : T.shareCopyFailed;
    if (ok) b.disabled = true;
  });
}

/** Preview a rendered result/badges image with share, copy, and download. */
export function openShareModal(img: ResultImage, title: string = t().shareModalTitle): void {
  const T = t();
  // Only offer the share button where the platform can share a file; the copy
  // button only where the clipboard accepts images. Download is always there.
  const shareBtn = canDeviceShare(img)
    ? `<button class="btn primary" id="mShare">${T.shareVia}</button>`
    : "";
  const copyBtn = canCopyImage()
    ? `<button class="btn" id="mCopy">${T.shareCopy}</button>`
    : "";
  const { overlay, close } = openModal(`<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(title)}">
      <h3 class="modalTitle">${esc(title)}</h3>
      <img class="shareImg" src="${img.dataUrl}" alt="${esc(T.shareModalTitle)}" />
      <div class="modalActions">
        ${shareBtn}
        ${copyBtn}
        <button class="btn" id="mDownload">${T.shareDownload}</button>
      </div>
      <button class="btn ghost modalClose" id="mClose">${T.shareClose}</button>
    </div>`);
  overlay.querySelector<HTMLButtonElement>("#mClose")?.addEventListener("click", close);

  overlay.querySelector<HTMLButtonElement>("#mShare")?.addEventListener("click", async () => {
    const result = await shareViaDevice(img);
    if (result === "shared" || result === "downloaded") close();
  });
  overlay.querySelector<HTMLButtonElement>("#mCopy")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const ok = await copyImageToClipboard(img);
    b.textContent = ok ? T.shareCopied : T.shareCopyFailed;
    if (ok) b.disabled = true;
  });
  overlay.querySelector<HTMLButtonElement>("#mDownload")?.addEventListener("click", () => {
    downloadImage(img);
  });
}
